import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusChange from './StatusChange';
import Button from 'src/ui/wards/common/button/Button';
import { useNavigate } from 'react-router-dom';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import {
  RequestDetailsOptionFactory,
  RequestDetailsOption,
} from 'src/api/wa/store/models/statusChange/StatusChange';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import { useWaLocationService } from 'src/api/wa/service/WaLocationService';
import { useWaProductStore } from 'src/api/wa/store/WaProductStore';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';

export function findAndMap<T, R>(
  array: T[] | null | undefined,
  predicate: (item: T) => boolean,
  mapper: (item: T) => R
): R | null {
  const found = array?.find(predicate);
  return found ? mapper(found) : null;
}

export default function S4_2_1_StatusBulkChange() {
  const { t } = useTranslation();
  const { products } = useWaProductStore();
  const { selectedWorkplace } = useWaWorkplaceStore();
  const { updateProductStatus, reloadhWorkplace } = useWaWorkplaceService();
  const { selectedDisaster } = useWaDisasterStore();
  const navigate = useNavigate();
  const [newComment, setNewComment] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { updateWaUserLocation } = useWaLocationService();
  const notify = useNotify();
  const userAgent = useUserAgent();

  const currentStatusId = selectedWorkplace?.details?.statusId ?? '00';
  const statusOptions = RequestDetailsOptionFactory.generateSet(currentStatusId);
  const getDefaultStatus = (id: string): string => {
    if (id === '65') return '26';

    const recoveryStatuses = ['41', '80', '46', '49', '45', '28', '54', '43'];
    if (recoveryStatuses.includes(id)) return '22';

    return '01';
  };

  const [status, setStatus] = useState<string>(getDefaultStatus(currentStatusId));

  const handleUpdateStatus = async () => {
    if (!products || !selectedWorkplace) return false;

    const productNumbers = products.map((product) => product.productNumber);
    const updatedComment = newComment;

    // To know comment only update, add prefix '9' for the status
    const statusToSend =
      status === RequestDetailsOption.updateOnlyComment
        ? '9' + (selectedWorkplace.details?.statusId ?? '')
        : status;

    try {
      const res = await updateProductStatus(
        selectedWorkplace.customerId,
        selectedWorkplace.branchCode,
        productNumbers,
        statusToSend,
        updatedComment ?? undefined
      );
      if (!res) return false;

      if (userAgent.isMobile) {
        updateWaUserLocation();
      }

      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      reloadhWorkplace(
        selectedDisaster?.id ? selectedDisaster.id : '',
        selectedWorkplace?.branchCode,
        selectedWorkplace?.customerId
      );
    }
  };

  const handleOkAction = async () => {
    if (!selectedWorkplace) {
      navigate(-1);
      return;
    }

    if (isLoading) return;
    setIsLoading(true);
    const success = await handleUpdateStatus();

    if (success) {
      notify('wards.common.updateSuccess', () => {
        navigate(-1);
      });
    } else {
      notify('wards.common.updateError');
      setIsLoading(false);
    }
  };

  const backNavigation = () => {
    navigate(-1);
  };

  const handleCancelAction = () => {
    navigate(-1);
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 255) {
      setNewComment(value);
    }
    if (value.length === 255) {
      notify(t('wards.common.maxLengthReached'));
    }
  };

  useEffect(() => {
    setNewComment(selectedWorkplace?.details?.comment ?? '');
  }, [selectedWorkplace?.details?.comment]);

  return (
    <div className="flex min-h-screen flex-col space-y-2 bg-gray-100 px-4 py-8">
      <div className="mx-auto w-full max-w-xl px-4">
        <h2 className="text-left text-2xl font-bold">{t('wards.statusChange.title')}</h2>
        <div className="flex justify-start pt-4">
          <Button title={t('wards.common.back')} onClick={backNavigation} />
        </div>
        <StatusChange
          status={findAndMap(
            statusOptions.list,
            (u) => u === status,
            (u) => ({
              placeholderTranslationKey: RequestDetailsOptionFactory.getOptionTranslationKey(u),
              value: String(u),
            })
          )}
          statusList={statusOptions.list.map((item) => ({
            placeholderTranslationKey: RequestDetailsOptionFactory.getOptionTranslationKey(item),
            value: String(item),
          }))}
          setStatus={(item) => setStatus(item.value as string)}
          isLoading={isLoading}
          handleOkAction={handleOkAction}
          handleCancelAction={handleCancelAction}
          bulletPoints={[
            {
              placeholderTranslationKey: 'wards.common.status',
              value: selectedWorkplace?.details?.statusId
                ? t(getDetailStatusTranslationKey(selectedWorkplace?.details?.statusId))
                : '',
            },
            {
              placeholderTranslationKey: 'wards.common.customerNumber',
              value: selectedWorkplace?.customerId ?? '',
            },
            {
              placeholderTranslationKey: 'wards.desktop.unaddressedPropertiesList.buildingName',
              value:
                (selectedWorkplace?.emergencyClass ? '★' : '') + (selectedWorkplace?.name ?? ''),
            },
            {
              placeholderTranslationKey: 'wards.desktop.unaddressedPropertiesList.comment',
              value: (
                <>
                  <textarea
                    value={newComment}
                    onChange={handleCommentChange}
                    rows={3}
                    maxLength={255}
                    className="w-full rounded-md border p-2.5"
                  />
                  <div className="mt-1 text-right text-xs text-gray-500">
                    {255 - newComment.length} / 255
                  </div>
                </>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
